import React, { useState } from 'react';
import {
   Box,
   Typography,
   Button,
   Container,
   Grid,
   Card,
   CardContent,
   AppBar,
   Toolbar,
   IconButton,
   useTheme,
   useMediaQuery,
   alpha,
} from '@mui/material';
import {
   Menu as MenuIcon,
   Close as CloseIcon,
   CalendarToday as CalendarIcon,
   PhotoCamera as CameraIcon,
   LocationOn as MapPinIcon,
   Groups as UsersIcon,
   Smartphone,
   Cloud,
   Star as StarIcon,
   Rocket as RocketIcon,
   PlayArrow as PlayIcon,
   Check as CheckIcon,
   FlashOn as ZapIcon,
   AutoFixHigh as Wand2Icon,
   ArrowForward as ArrowRightIcon,
   Schedule as ClockIcon,
   Public as GlobeIcon,
   Favorite as HeartIcon,
   AutoAwesome as SparklesIcon,
   Visibility as EyeIcon,
   Share as Share2Icon,
   Hub as NetworkIcon,
   PersonAdd as UserPlusIcon,
   EmojiEvents as AwardIcon,
   Adjust as TargetIcon,
   ArrowBack as ArrowLeftIcon,
   Psychology as BrainIcon,
   Image as ImageIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';

const Features: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const navigate = useNavigate();
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

   return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
         {/* Navigation */}
         <AppBar
            position='fixed'
            sx={{
               background:
                  'linear-gradient(135deg, rgba(1, 148, 154, 0.95) 0%, rgba(0, 67, 105, 0.95) 100%)',
               backdropFilter: 'blur(15px)',
               boxShadow: '0 2px 15px rgba(1, 148, 154, 0.2)',
               zIndex: 1100,
               color: 'white',
               border: 'none',
               borderRadius: 0,
            }}
         >
            <Toolbar sx={{ justifyContent: 'space-between', py: 0.5 }}>
               <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                  onClick={() => navigate('/')}
               >
                  <img
                     src={logo}
                     alt='WeTrippen Logo'
                     style={{ width: 32, height: 32, borderRadius: 6 }}
                  />
                  <Typography
                     variant='h6'
                     sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}
                  >
                     WeTrippen
                  </Typography>
               </Box>

               {!isMobile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                     <Button
                        component={RouterLink}
                        to='/login'
                        sx={{
                           color: 'rgba(255,255,255,0.9)',
                           textTransform: 'none',
                           fontSize: '0.9rem',
                           fontWeight: 400,
                        }}
                     >
                        Sign In
                     </Button>
                  </Box>
               ) : (
                  <IconButton
                     onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                     sx={{ color: 'white' }}
                  >
                     {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
               )}
            </Toolbar>

            {/* Mobile Menu */}
            {isMobile && mobileMenuOpen && (
               <Box sx={{ bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Button
                     fullWidth
                     component={RouterLink}
                     to='/login'
                     sx={{ justifyContent: 'flex-start', mb: 1, color: 'text.primary' }}
                  >
                     Sign In
                  </Button>
               </Box>
            )}
         </AppBar>

         {/* Hero Section */}
         <Box
            sx={{
               pt: 20,
               pb: 10,
               background:
                  'linear-gradient(135deg, rgba(1, 148, 154, 0.1) 0%, rgba(0, 67, 105, 0.1) 50%, rgba(255, 107, 107, 0.1) 100%)',
            }}
         >
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center' }}>
                  <Typography
                     variant='h2'
                     sx={{
                        fontWeight: 700,
                        mb: 3,
                        background: theme.palette.gradient?.primary,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                     }}
                  >
                     Every Feature Designed to Build Stronger Connections
                  </Typography>
                  <Typography variant='h5' sx={{ color: 'text.secondary', mb: 4, fontWeight: 500 }}>
                     Stop Juggling 10 Apps to Plan One Great Experience
                  </Typography>
                  <Typography
                     variant='body1'
                     sx={{
                        color: 'text.secondary',
                        mb: 6,
                        maxWidth: 600,
                        mx: 'auto',
                        lineHeight: 1.7,
                     }}
                  >
                     Everything you need to create, coordinate, and capture unforgettable moments -
                     all in one beautiful platform designed for people who take relationships
                     seriously.
                  </Typography>
               </Box>
            </Container>
         </Box>

         {/* Event & Trip Creation */}
         <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
            <Container maxWidth='lg'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     Create Events That People Actually Want to Attend
                  </Typography>
               </Box>

               <Grid container spacing={8} sx={{ mb: 10 }}>
                  {/* Smart Event Creation */}
                  <Grid item xs={12} lg={6}>
                     <Card
                        sx={{
                           p: 4,
                           height: '100%',
                           background:
                              'linear-gradient(135deg, rgba(1, 148, 154, 0.05) 0%, rgba(0, 67, 105, 0.05) 100%)',
                           boxShadow: '0 8px 30px rgba(1, 148, 154, 0.15)',
                        }}
                     >
                        <CardContent sx={{ p: 0 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                              <Box
                                 sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'primary.main',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 2,
                                 }}
                              >
                                 <RocketIcon sx={{ color: 'white', fontSize: 24 }} />
                              </Box>
                              <Box>
                                 <Typography variant='h5' sx={{ fontWeight: 600, mb: 1 }}>
                                    Smart Event Creation
                                 </Typography>
                                 <Typography
                                    variant='subtitle1'
                                    sx={{ color: 'primary.main', fontWeight: 600 }}
                                 >
                                    "From Idea to Invitation in Under 2 Minutes"
                                 </Typography>
                              </Box>
                           </Box>

                           <Typography
                              variant='body1'
                              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}
                           >
                              Stop spending hours planning events that feel like work. WeTrippen's
                              intelligent event creation helps you build compelling experiences that
                              people can't wait to join.
                           </Typography>

                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                              {[
                                 {
                                    icon: <BrainIcon />,
                                    title: 'One-click AI cover photos',
                                    desc: 'Never use a boring stock image again. Generate stunning, relevant cover art that makes your event irresistible',
                                 },
                                 {
                                    icon: <Wand2Icon />,
                                    title: 'AI-powered descriptions',
                                    desc: 'Struggling with what to write? Our AI creates engaging event descriptions that capture excitement and drive RSVPs',
                                 },
                                 {
                                    icon: <TargetIcon />,
                                    title: 'Smart invitation targeting',
                                    desc: 'Invite the right mix of people based on past events, interests, and connection strength',
                                 },
                                 {
                                    icon: <AwardIcon />,
                                    title: 'Professional presentation',
                                    desc: 'Every event looks polished and well-planned, even if you created it in minutes',
                                 },
                              ].map((feature, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                                 >
                                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                                       {feature.icon}
                                    </Box>
                                    <Box>
                                       <Typography
                                          variant='subtitle2'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {feature.title}
                                       </Typography>
                                       <Typography
                                          variant='body2'
                                          sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                                       >
                                          {feature.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>

                           <Box sx={{ bgcolor: alpha('#fff', 0.8), p: 2, borderRadius: 2 }}>
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontWeight: 500,
                                    color: 'primary.main',
                                    textAlign: 'center',
                                 }}
                              >
                                 "While others send boring text invites to half-empty events, you'll
                                 be the organizer known for experiences people fight to attend."
                              </Typography>
                           </Box>
                        </CardContent>
                     </Card>
                  </Grid>

                  {/* Trip Planning */}
                  <Grid item xs={12} lg={6}>
                     <Card
                        sx={{
                           p: 4,
                           height: '100%',
                           background:
                              'linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 87, 34, 0.05) 100%)',
                           boxShadow: '0 8px 30px rgba(255, 152, 0, 0.15)',
                        }}
                     >
                        <CardContent sx={{ p: 0 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                              <Box
                                 sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'warning.main',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 2,
                                 }}
                              >
                                 <MapPinIcon sx={{ color: 'white', fontSize: 24 }} />
                              </Box>
                              <Box>
                                 <Typography variant='h5' sx={{ fontWeight: 600, mb: 1 }}>
                                    Trip Planning That Builds Anticipation
                                 </Typography>
                                 <Typography
                                    variant='subtitle1'
                                    sx={{ color: 'warning.main', fontWeight: 600 }}
                                 >
                                    "Turn Travel Planning Into Part of the Adventure"
                                 </Typography>
                              </Box>
                           </Box>

                           <Typography
                              variant='body1'
                              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}
                           >
                              Your trip should be exciting from the moment you announce it.
                              WeTrippen makes trip planning so engaging that people start bonding
                              before they even pack.
                           </Typography>

                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                              {[
                                 {
                                    icon: <GlobeIcon />,
                                    title: 'Visual trip mapping',
                                    desc: 'See your adventure unfold on beautiful maps that build excitement and help with planning',
                                 },
                                 {
                                    icon: <UsersIcon />,
                                    title: 'Collaborative itinerary building',
                                    desc: "Let everyone contribute ideas and feel invested in the trip's success",
                                 },
                                 {
                                    icon: <HeartIcon />,
                                    title: 'Anticipation building tools',
                                    desc: 'Share countdown timers, destination highlights, and pre-trip conversations that get everyone excited',
                                 },
                                 {
                                    icon: <CameraIcon />,
                                    title: 'Memory preservation setup',
                                    desc: 'Start capturing memories before you even leave home',
                                 },
                              ].map((feature, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                                 >
                                    <Box sx={{ color: 'warning.main', mt: 0.5 }}>
                                       {feature.icon}
                                    </Box>
                                    <Box>
                                       <Typography
                                          variant='subtitle2'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {feature.title}
                                       </Typography>
                                       <Typography
                                          variant='body2'
                                          sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                                       >
                                          {feature.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>

                           <Box sx={{ bgcolor: alpha('#fff', 0.8), p: 2, borderRadius: 2 }}>
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontWeight: 500,
                                    color: 'warning.main',
                                    textAlign: 'center',
                                 }}
                              >
                                 "Transform trip planning from a chore into the beginning of your
                                 adventure story."
                              </Typography>
                           </Box>
                        </CardContent>
                     </Card>
                  </Grid>
               </Grid>
            </Container>
         </Box>

         {/* Organization & Visualization */}
         <Box sx={{ py: 10, bgcolor: 'grey.50' }}>
            <Container maxWidth='lg'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     See Your Social Life the Way It Really Connects
                  </Typography>
               </Box>

               <Grid container spacing={8}>
                  {/* Calendar & Timeline */}
                  <Grid item xs={12} lg={6}>
                     <Card sx={{ p: 4, height: '100%', boxShadow: '0 8px 30px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 0 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                              <Box
                                 sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'success.main',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 2,
                                 }}
                              >
                                 <CalendarIcon sx={{ color: 'white', fontSize: 24 }} />
                              </Box>
                              <Box>
                                 <Typography variant='h5' sx={{ fontWeight: 600, mb: 1 }}>
                                    Calendar & Timeline Views
                                 </Typography>
                                 <Typography
                                    variant='subtitle1'
                                    sx={{ color: 'success.main', fontWeight: 600 }}
                                 >
                                    "Never Miss a Moment or Double-Book Again"
                                 </Typography>
                              </Box>
                           </Box>

                           <Typography
                              variant='body1'
                              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}
                           >
                              Your social calendar deserves better than scattered text messages and
                              forgotten commitments. See all your connections and commitments in one
                              beautiful, organized view.
                           </Typography>

                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                              {[
                                 {
                                    icon: <Smartphone />,
                                    title: 'Intelligent calendar integration',
                                    desc: 'All your events, trips, and social commitments in one gorgeous view',
                                 },
                                 {
                                    icon: <ClockIcon />,
                                    title: 'Timeline storytelling',
                                    desc: 'Watch your social life unfold as a beautiful story of connections and experiences',
                                 },
                                 {
                                    icon: <ZapIcon />,
                                    title: 'Conflict prevention',
                                    desc: 'Smart scheduling that prevents you from overwhelming yourself or missing important moments',
                                 },
                                 {
                                    icon: <NetworkIcon />,
                                    title: 'Connection tracking',
                                    desc: 'See how often you connect with different groups and individuals',
                                 },
                              ].map((feature, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                                 >
                                    <Box sx={{ color: 'success.main', mt: 0.5 }}>
                                       {feature.icon}
                                    </Box>
                                    <Box>
                                       <Typography
                                          variant='subtitle2'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {feature.title}
                                       </Typography>
                                       <Typography
                                          variant='body2'
                                          sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                                       >
                                          {feature.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>

                           <Box
                              sx={{
                                 bgcolor: alpha(theme.palette.success.main, 0.1),
                                 p: 2,
                                 borderRadius: 2,
                              }}
                           >
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontWeight: 500,
                                    color: 'success.main',
                                    textAlign: 'center',
                                 }}
                              >
                                 "Finally, a social calendar that makes your life look as amazing as
                                 it actually is."
                              </Typography>
                           </Box>
                        </CardContent>
                     </Card>
                  </Grid>

                  {/* Interactive Trip Mapping */}
                  <Grid item xs={12} lg={6}>
                     <Card sx={{ p: 4, height: '100%', boxShadow: '0 8px 30px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 0 }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                              <Box
                                 sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'secondary.main',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 2,
                                 }}
                              >
                                 <GlobeIcon sx={{ color: 'white', fontSize: 24 }} />
                              </Box>
                              <Box>
                                 <Typography variant='h5' sx={{ fontWeight: 600, mb: 1 }}>
                                    Interactive Trip Mapping
                                 </Typography>
                                 <Typography
                                    variant='subtitle1'
                                    sx={{ color: 'secondary.main', fontWeight: 600 }}
                                 >
                                    "Turn Every Journey Into a Visual Adventure Story"
                                 </Typography>
                              </Box>
                           </Box>

                           <Typography
                              variant='body1'
                              sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}
                           >
                              Your travels deserve better documentation than random pins on generic
                              maps. Create stunning visual stories that make people want to join
                              your next adventure.
                           </Typography>

                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                              {[
                                 {
                                    icon: <EyeIcon />,
                                    title: 'Beautiful map visualization',
                                    desc: 'See all your trips plotted on gorgeous, interactive maps',
                                 },
                                 {
                                    icon: <StarIcon />,
                                    title: 'Story building',
                                    desc: 'Each location becomes a chapter in your ongoing adventure narrative',
                                 },
                                 {
                                    icon: <SparklesIcon />,
                                    title: 'Inspiration for others',
                                    desc: 'Your map becomes a source of travel inspiration for friends and family',
                                 },
                                 {
                                    icon: <Share2Icon />,
                                    title: 'Easy sharing',
                                    desc: 'Show off your adventures with beautiful map views that impress everyone',
                                 },
                              ].map((feature, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                                 >
                                    <Box sx={{ color: 'secondary.main', mt: 0.5 }}>
                                       {feature.icon}
                                    </Box>
                                    <Box>
                                       <Typography
                                          variant='subtitle2'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {feature.title}
                                       </Typography>
                                       <Typography
                                          variant='body2'
                                          sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                                       >
                                          {feature.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>

                           <Box
                              sx={{
                                 bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                 p: 2,
                                 borderRadius: 2,
                              }}
                           >
                              <Typography
                                 variant='body2'
                                 sx={{
                                    fontWeight: 500,
                                    color: 'secondary.main',
                                    textAlign: 'center',
                                 }}
                              >
                                 "While others have boring lists of places they've been, you'll have
                                 an inspiring visual story of your adventures."
                              </Typography>
                           </Box>
                        </CardContent>
                     </Card>
                  </Grid>
               </Grid>
            </Container>
         </Box>

         {/* Photo Management & AI Enhancement */}
         <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
            <Container maxWidth='lg'>
               <Box sx={{ textAlign: 'center', mb: 8 }}>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 3 }}>
                     Transform Ordinary Photos Into Extraordinary Memories
                  </Typography>
               </Box>

               <Grid container spacing={6}>
                  {[
                     {
                        icon: <Wand2Icon />,
                        title: 'Instant AI Photo Enhancement',
                        subtitle: '"Make Every Photo Look Like You Hired a Professional"',
                        description:
                           "Stop settling for blurry, poorly lit photos that don't capture how amazing your experiences actually were.",
                        features: [
                           {
                              title: 'One-click perfection',
                              desc: 'Transform any photo with a single tap',
                           },
                           {
                              title: 'Multiple enhancement styles',
                              desc: 'Subtle improvements to dramatic transformations',
                           },
                        ],
                        quote: 'Never apologize for a bad photo again.',
                        color: 'error',
                     },
                     {
                        icon: <ImageIcon />,
                        title: 'Smart Album Creation',
                        subtitle: '"Automatically Organize Your Best Memories"',
                        description:
                           'Stop letting your best photos get buried in camera rolls. WeTrippen automatically creates stunning albums.',
                        features: [
                           {
                              title: 'AI-powered curation',
                              desc: 'Automatically selects your best photos',
                           },
                           {
                              title: 'Story-driven organization',
                              desc: 'Albums follow natural experience flow',
                           },
                        ],
                        quote: 'Curated albums that actually get viewed and shared.',
                        color: 'info',
                     },
                     {
                        icon: <Share2Icon />,
                        title: 'Effortless Photo Sharing',
                        subtitle: '"Share Memories as Easily as Taking Them"',
                        description:
                           "Your amazing photos shouldn't sit trapped in your phone. Share beautiful memories instantly and effortlessly.",
                        features: [
                           {
                              title: 'One-click album sharing',
                              desc: 'Share entire collections instantly',
                           },
                           {
                              title: 'Smart recipient suggestions',
                              desc: 'Suggests who should receive each album',
                           },
                        ],
                        quote: 'Be the person who actually shares great photos.',
                        color: 'success',
                     },
                  ].map((feature, index) => (
                     <Grid item xs={12} md={4} key={index}>
                        <Card
                           sx={{
                              p: 3,
                              height: '100%',
                              background: `linear-gradient(135deg, ${alpha(
                                 feature.color === 'error'
                                    ? theme.palette.error.main
                                    : feature.color === 'info'
                                      ? theme.palette.info.main
                                      : feature.color === 'success'
                                        ? theme.palette.success.main
                                        : theme.palette.primary.main,
                                 0.05
                              )} 0%, ${alpha(
                                 feature.color === 'error'
                                    ? theme.palette.error.main
                                    : feature.color === 'info'
                                      ? theme.palette.info.main
                                      : feature.color === 'success'
                                        ? theme.palette.success.main
                                        : theme.palette.primary.main,
                                 0.1
                              )} 100%)`,
                              boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                           }}
                        >
                           <CardContent sx={{ p: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                 <Box
                                    sx={{
                                       width: 48,
                                       height: 48,
                                       bgcolor: `${feature.color}.main`,
                                       borderRadius: '50%',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       mr: 2,
                                    }}
                                 >
                                    {React.cloneElement(feature.icon, {
                                       sx: { color: 'white', fontSize: 24 },
                                    })}
                                 </Box>
                                 <Box>
                                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                                       {feature.title}
                                    </Typography>
                                    <Typography
                                       variant='caption'
                                       sx={{ color: `${feature.color}.main`, fontWeight: 600 }}
                                    >
                                       {feature.subtitle}
                                    </Typography>
                                 </Box>
                              </Box>

                              <Typography
                                 variant='body2'
                                 sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.5 }}
                              >
                                 {feature.description}
                              </Typography>

                              <Box
                                 sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}
                              >
                                 {feature.features.map((item, itemIndex) => (
                                    <Box
                                       key={itemIndex}
                                       sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                                    >
                                       <CheckIcon
                                          sx={{
                                             color: `${feature.color}.main`,
                                             fontSize: 16,
                                             mt: 0.25,
                                          }}
                                       />
                                       <Box>
                                          <Typography
                                             variant='caption'
                                             sx={{ fontWeight: 600, display: 'block' }}
                                          >
                                             {item.title}
                                          </Typography>
                                          <Typography
                                             variant='caption'
                                             sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                                          >
                                             {item.desc}
                                          </Typography>
                                       </Box>
                                    </Box>
                                 ))}
                              </Box>

                              <Box sx={{ bgcolor: alpha('#fff', 0.8), p: 1.5, borderRadius: 1 }}>
                                 <Typography
                                    variant='caption'
                                    sx={{ fontWeight: 500, color: `${feature.color}.main` }}
                                 >
                                    "{feature.quote}"
                                 </Typography>
                              </Box>
                           </CardContent>
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            </Container>
         </Box>

         {/* Final Conversion Section */}
         <Box
            sx={{
               py: 10,
               background: theme.palette.gradient?.primary,
               color: 'white',
               textAlign: 'center',
            }}
         >
            <Container maxWidth='md'>
               <Typography variant='h2' sx={{ fontWeight: 700, mb: 3, color: 'white' }}>
                  Ready to Transform How You Connect?
               </Typography>
               <Typography variant='h6' sx={{ mb: 4, color: alpha('#fff', 0.9) }}>
                  Join 2,847 organizers who've discovered what happens when you stop settling for
                  scattered photos, forgotten connections, and amateur-looking events.
               </Typography>

               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: { xs: 'column', sm: 'row' },
                     gap: 2,
                     justifyContent: 'center',
                     mb: 6,
                  }}
               >
                  <Button
                     size='large'
                     sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        '&:hover': {
                           bgcolor: 'grey.100',
                        },
                     }}
                  >
                     Get All Features Now - $35/year
                  </Button>
                  <Button
                     size='large'
                     variant='outlined'
                     sx={{
                        borderColor: 'white',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        '&:hover': {
                           bgcolor: alpha('#fff', 0.1),
                           borderColor: 'white',
                        },
                     }}
                  >
                     See Features in Action
                  </Button>
               </Box>

               <Box
                  sx={{
                     bgcolor: alpha('#fff', 0.1),
                     backdropFilter: 'blur(10px)',
                     p: 3,
                     borderRadius: 2,
                     mb: 4,
                  }}
               >
                  <Typography variant='body1' sx={{ fontWeight: 500, mb: 2 }}>
                     "89% of WeTrippen users say it's transformed how they build and maintain
                     relationships. The other 11% say it's the best $35 they've ever spent."
                  </Typography>
               </Box>

               <Typography variant='body1' sx={{ color: alpha('#fff', 0.9) }}>
                  Your next event deserves better than scattered group chats and forgotten photos.
                  Start building lasting connections today.
               </Typography>
            </Container>
         </Box>
      </Box>
   );
};

export default Features;
